/**
 * SortableHeader — clickable column header that signals current sort
 * direction with an up/down arrow. Stateless; the caller owns the sort
 * state (usually backed by URL search params).
 *
 * Use inside a DataTable column's `header` slot:
 *
 *   columns = [{
 *     id: 'name',
 *     header: (
 *       <SortableHeader
 *         label="Name"
 *         active={sortBy === 'name'}
 *         direction={sortDir}
 *         onToggle={() => setSort('name')}
 *       />
 *     ),
 *     cell: (r) => r.name,
 *   }]
 *
 * Clicking when not active sorts ascending. Clicking when active flips
 * direction. (We don't expose a "no-sort" third state — the URL always
 * has a default sort.)
 */

import { ChevronDown, ChevronUp } from 'lucide-react';

export type SortDirection = 'asc' | 'desc';

export interface SortableHeaderProps {
  label: string;
  active: boolean;
  direction: SortDirection;
  onToggle: () => void;
  /** Right-align — match the parent column's numeric flag. */
  numeric?: boolean;
}

export function SortableHeader({
  label,
  active,
  direction,
  onToggle,
  numeric = false,
}: SortableHeaderProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      // The native <th> already does font/uppercase styling — we just need
      // the button to inherit that and reveal the indicator on hover/active.
      className={`group inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide ${
        active ? 'text-primary' : 'text-secondary hover:text-primary'
      } ${numeric ? 'flex-row-reverse' : ''}`}
      aria-label={`Sort by ${label} ${active && direction === 'asc' ? 'descending' : 'ascending'}`}
    >
      <span>{label}</span>
      <span className="inline-flex h-3 w-3 items-center justify-center">
        {active ? (
          direction === 'asc' ? (
            <ChevronUp className="h-3 w-3" strokeWidth={2} />
          ) : (
            <ChevronDown className="h-3 w-3" strokeWidth={2} />
          )
        ) : (
          // Subtle indicator on hover only — keeps non-active headers from
          // looking visually busy.
          <ChevronDown
            className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-50"
            strokeWidth={2}
          />
        )}
      </span>
    </button>
  );
}
